/**
 * Phase2BoardGrid.tsx
 * Board-view sibling of Phase2WeeklyGrid: same table skeleton (sticky task gutter +
 * week columns on the shared rail geometry, synchronized hover "beam", baseline ghost
 * layer, SkillGate/Overbook escalation dialog) — but the gutter shows a test-type icon
 * + bold label and each cell renders the compact Phase2BoardCell.
 */

import React, { useCallback, useMemo, useState } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import DateRangeIcon from '@mui/icons-material/DateRange';
import { useTheme, alpha } from '@mui/material/styles';

import { Phase2BoardCell } from './Phase2BoardCell';
import { displayTaskName, showTypeSubtitle } from './taskDisplayName';
import { iconForTaskType } from './testTypeIcons';
import { AssignEmployeesDialog } from '../../tasks/AssignEmployeesDialog';
import { useBaselineAssignments } from './useBaselineAssignments';
import { useGraphPhotos } from '../../../../hooks/useGraphPhotos';
import type { Phase2WeeklyGridProps } from './Phase2WeeklyGrid';
import type { TaskWithBookings } from '../../../../services/scheduleTasks';
import type { AssignableUser } from '../../../../services/scheduleAssignments';
import { weekStartsInRange } from '../../../../utils/weekUtils';
import { RAIL_GUTTER_W, RAIL_WEEK_W } from './phase2RailGeometry';

const TASK_COL_W = RAIL_GUTTER_W;
const WEEK_COL_W = RAIL_WEEK_W;

/** Sorted BST week-start Saturdays spanning all tasks (same as the Classic grid). */
function computeWeekColumns(tasks: TaskWithBookings[]): string[] {
  let minStart: string | null = null;
  let maxEnd: string | null = null;
  for (const t of tasks) {
    if (t.StartDate && (!minStart || t.StartDate < minStart)) minStart = t.StartDate;
    if (t.EndDate && (!maxEnd || t.EndDate > maxEnd)) maxEnd = t.EndDate;
  }
  if (!minStart || !maxEnd) return [];
  return weekStartsInRange(minStart, maxEnd);
}

export const Phase2BoardGrid: React.FC<Phase2WeeklyGridProps> = ({
  tasks,
  assignmentMap,
  scenarioKey,
  onAssignmentsChanged,
  onAllocationChanged,
  compareToBaseline,
  hoveredWeek,
  onHoverWeek,
  holidays,
}) => {
  const theme = useTheme();
  const beamRail = `2px solid ${theme.schedule.selection}`;
  const weekColumns = computeWeekColumns(tasks);
  const baselineAssignmentMap = useBaselineAssignments(tasks, compareToBaseline);

  // Real Graph profile photos for every assigned person (best-effort, cached) — the
  // Board avatars show the pic when available, initials otherwise.
  const assigneeEmails = useMemo(() => {
    const set = new Set<string>();
    for (const rows of Object.values(assignmentMap)) {
      for (const r of rows) if (r.email) set.add(r.email);
    }
    return Array.from(set);
  }, [assignmentMap]);
  const photoByEmail = useGraphPhotos(assigneeEmails);

  // ── Dialog escalation (SkillGate / Overbook) — same wiring as the Classic grid ──
  const [dialogTask, setDialogTask] = useState<TaskWithBookings | null>(null);
  const [dialogPreselect, setDialogPreselect] = useState<AssignableUser | null>(null);
  const [dialogBookingKey, setDialogBookingKey] = useState<string | null>(null);

  const handleEscalateToDialog = useCallback(
    (task: TaskWithBookings, preselectUser?: AssignableUser, machineBookingKey?: string | null) => {
      setDialogTask(task);
      setDialogPreselect(preselectUser ?? null);
      setDialogBookingKey(machineBookingKey ?? null);
    },
    [],
  );
  const handleCloseDialog = useCallback(() => {
    setDialogTask(null);
    setDialogPreselect(null);
    setDialogBookingKey(null);
  }, []);
  const handleDialogSaved = useCallback(() => {
    if (dialogTask) onAssignmentsChanged(dialogTask.ScheduleTaskKey);
  }, [dialogTask, onAssignmentsChanged]);
  const handleDialogAssigneesChanged = useCallback(() => {
    if (dialogTask) onAssignmentsChanged(dialogTask.ScheduleTaskKey);
  }, [dialogTask, onAssignmentsChanged]);

  if (tasks.length === 0) return null;
  if (weekColumns.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary', border: `1px dashed ${theme.border.color}`, borderRadius: `${theme.border.radius}px` }}>
        <DateRangeIcon sx={{ fontSize: 36, opacity: 0.3 }} />
        <Typography variant="body2" sx={{ mt: 1 }}>
          Assign machines to tasks in Phase 1 to see the weekly schedule here.
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <AssignEmployeesDialog
        open={dialogTask !== null}
        task={dialogTask}
        preselectUser={dialogPreselect}
        onClose={handleCloseDialog}
        onSaved={handleDialogSaved}
        onAssigneesChanged={handleDialogAssigneesChanged}
        scenarioKey={scenarioKey ?? undefined}
        machineBookingKey={dialogBookingKey}
      />

      <Box
        onMouseLeave={() => onHoverWeek?.(null)}
        sx={{
          border: `1px solid ${theme.border.color}`,
          borderRadius: `${theme.border.radius}px`,
          overflow: 'visible',
          backgroundColor: theme.palette.background.paper,
          width: 'fit-content',
        }}
      >
        <Table size="small" sx={{ tableLayout: 'fixed', width: TASK_COL_W + weekColumns.length * WEEK_COL_W }}>
          <TableBody>
            {tasks.map((task, rowIdx) => {
              const taskAssignments = assignmentMap[task.ScheduleTaskKey] ?? [];
              const taskBaseline = baselineAssignmentMap[task.ScheduleTaskKey];
              const rowBg = rowIdx % 2 === 1 ? theme.table.stripeBg : undefined;
              const Icon = iconForTaskType(task);
              return (
                <TableRow key={task.ScheduleTaskKey} sx={{ '& td': { verticalAlign: 'top' }, backgroundColor: rowBg }}>
                  {/* Task gutter — test-type icon + bold name + optional subtitle (sticky). */}
                  <TableCell
                    sx={{
                      position: 'sticky', left: 0, zIndex: 1,
                      backgroundColor: rowBg ?? theme.palette.background.paper,
                      // Frozen-column divider via inset shadow (borderRight collapses on scroll).
                      boxShadow: `inset -1px 0 0 0 ${theme.border.color}, 6px 0 6px -6px ${alpha(theme.palette.text.primary, 0.18)}`,
                      py: 1, px: 1.5, width: TASK_COL_W,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, minWidth: 0 }}>
                      <Box
                        sx={{
                          flexShrink: 0, mt: 0.2, width: 26, height: 26, borderRadius: '6px',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          backgroundColor: alpha(theme.schedule.machineTime, 0.12),
                          color: theme.schedule.machineTime,
                        }}
                      >
                        <Icon sx={{ fontSize: 16 }} />
                      </Box>
                      <Tooltip title={task.TaskTypeName ?? ''} placement="right">
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" fontWeight={700} sx={{ fontSize: '0.82rem' }} noWrap>
                            {displayTaskName(task.Name, task.TaskTypeName)}
                          </Typography>
                          {showTypeSubtitle(task.Name, task.TaskTypeName) && (
                            <Typography variant="caption" color="text.secondary" noWrap>
                              {task.TaskTypeName}
                            </Typography>
                          )}
                        </Box>
                      </Tooltip>
                    </Box>
                  </TableCell>

                  {weekColumns.map((ws) => {
                    const hot = hoveredWeek === ws;
                    return (
                      <TableCell
                        key={ws}
                        onMouseEnter={() => onHoverWeek?.(ws)}
                        sx={{
                          px: 0.5, py: 0.5, width: WEEK_COL_W, verticalAlign: 'top',
                          borderRight: `1px solid ${theme.border.color}`,
                          ...(hot ? { backgroundColor: alpha(theme.schedule.selection, 0.06), borderLeft: beamRail, borderRight: beamRail } : {}),
                          transition: 'background-color 0.12s',
                        }}
                      >
                        <Phase2BoardCell
                          task={task}
                          weekStart={ws}
                          assignments={taskAssignments}
                          scenarioKey={scenarioKey}
                          onAssignmentsChanged={onAssignmentsChanged}
                          onAllocationChanged={onAllocationChanged}
                          onEscalateToDialog={handleEscalateToDialog}
                          baselineAssignments={taskBaseline}
                          compareToBaseline={compareToBaseline}
                          photoByEmail={photoByEmail}
                          holidays={holidays}
                        />
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>
    </>
  );
};
